import {CommonModule} from '@angular/common';
import {Component, Injector, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router, RouterModule} from '@angular/router';
import {forkJoin, Subject, takeUntil} from 'rxjs';
import {goBack} from 'src/app/common/common';
import {MetadataReportViewModel} from 'src/app/model/metadataReports.model';
import {Resource} from 'src/app/model/resources.model';
import {All, ChannelPowertypes, DisplayedPotentialRids, MarketContexts, Setup} from 'src/app/model/rids.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {SearchFilterModule} from '../../../common/searchFilter/searchFilter.module';
import {HttpParams} from "@angular/common/http";

@Component({
  selector: "app-import-rid",
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, SearchFilterModule],
  templateUrl: "./import-rid.component.html",
  styleUrl: "./import-rid.component.scss",
})
export class ImportRidComponent implements OnInit {
  public error: string | null = null;
  //search
  searchText: string = '';
  itemsByPage: number = 10;

  unsubscribe = new Subject<void>();
  formsSubscribe: any;
  // FORM
  importSetup = new FormGroup({
    program: new FormControl<string>(""),
    operator: new FormControl<string>(""),
    importBy: new FormControl<string>(""),
    setup: new FormControl<string>(""),
    marketContext: new FormControl<string>(""),
    marketContextNewValue: new FormControl<string>(""),
    resource: new FormControl<string>(""),
    resourceNewValue: new FormControl<string>(""),
    powerType: new FormControl<string>(""),
    powerTypeNewValue: new FormControl<string | undefined>(""),
    metadata: new FormControl<string>(""),
    metadataNewValue: new FormControl<any>([]),
  });
  public setup: Setup = {
    external_id: 0,
    data_source: "",
    market_context: "",
    resource_id: 0,
    by: "",
    operator: "",
    program: "",
    id: "",
  };

  public dataSources: Record<string, { label: string, key: string, channel: string }> = {
    'x-energy': {'label': 'X-Energy', 'key': 'x-energy', 'channel': ''},
    'static': {'label': 'Static', 'key': 'static', 'channel': ''},
    'basic': {'label': 'BASIC', 'key': 'basic', 'channel': ''}
  };

  public channel_powertypes: ChannelPowertypes[] = [
    {
      hertz: "",
      uuid: "",
      description: "",
      itemLabel: 0,
      itemUnit: "",
      siScaleCode: "",
      voltage: "",
    },
  ];
  public all: All = {
    resource: "",
    market_context: "",
    metadata_reports: "",
    power_type: "",
  };
  public metadata_reports: MetadataReportViewModel[] = [
    {
      data_source: "",
      report_type: "",
      report_specifier_id: "",
      label: "",
      created_at: "",
      rids: [],
      registered_reports: [],
      updated_by: "",
      id: 0,
      include_report_rid_prefix: false,
      send_updates: false,
      report_style: "",
      update_type: "",
    },
  ];

  public displayed_potential_rids: DisplayedPotentialRids[] = [
    {
      metadata_reports: "",
      resource: "",
      market_context: "",
      included: 0,
      display_label: "",
      division: "",
      voltage: "",
      mrid: "",
      power_type: "",
      external_id: "",
    },
  ];
  public metadaSelected: any = [];
  public isSetup: boolean = false;
  public router: Router;
  public resource: Resource = {
    id: 0,
    resource_id: "",
    updated_by: "",
    program_unit: "",
    events: [],
    vens: [],
  };
  public market_contexts: MarketContexts[] = [
    {
      id: 0,
      program: "",
      operator: "",
      market_context: "",
    },
  ];
  public selectedMC: any = {
    program: "",
    operator: "",
    market_context: "",
  };
  public importRid: any = {
    mrids: [],
  };
  public status: string = 'Waiting.';
  public channels_map: any = {};

  constructor(
    private injector: Injector,
    private importRidsService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  setFormsDefaultValue() {
    this.importSetup.controls.program.setValue(this.selectedMC?.program);
    this.importSetup.controls.operator.setValue(this.selectedMC?.operator);
    this.importSetup.controls.setup.setValue(this.selectedMC?.product);
  }

  ngOnInit(): void {
    const importRidsQueryParams: CustomHttpParams = {
      'depth': 1,
    };
    const metadataReportsQueryParams: CustomHttpParams = {
      'depth': 1
    };
    const powerTypesQueryParams: CustomHttpParams = {
      'depth': 1
    };
    const channelQueryParams: CustomHttpParams = {
      'depth': 3
    };
    forkJoin([
      this.importRidsService
        .getMarketContext(importRidsQueryParams)
        .pipe(takeUntil(this.unsubscribe)),
      this.importRidsService.getPowerType(powerTypesQueryParams).pipe(takeUntil(this.unsubscribe)),
      this.importRidsService
        .getMetadataReports(metadataReportsQueryParams)
        .pipe(takeUntil(this.unsubscribe)),
      this.importRidsService.getChannel(channelQueryParams).pipe(takeUntil(this.unsubscribe)),
    ]).subscribe(([marketContext, powerType, metadaReport, channel]) => {
      if (marketContext) {
        this.market_contexts = marketContext.map((mc: MarketContexts) => mc);
      }
      if (channel) {
        // Build channels_map to match Venway2
        this.channels_map = {};
        channel.forEach((item: any) => {
          this.channels_map[item.id] = [];
          if (item.channel_powertypes && item.channel_powertypes.length) {
            item.channel_powertypes.forEach((cp: any) => {
              this.channels_map[item.id].push(cp);
            });
          }
        });

        this.channel_powertypes = channel
          .filter((cp: any) => cp.channel_powertypes && cp.channel_powertypes.length > 0)
          .flatMap((channel: any) => channel.channel_powertypes);
      }
      if (metadaReport) {
        this.metadata_reports = metadaReport.filter((mr: any) => mr);
      }
    });

    this.formsSubscribe = this.importSetup.controls.setup.valueChanges.subscribe((val: any) => {
      this.setup.id = val;
    });

    this.formsSubscribe = this.importSetup.controls.marketContext.valueChanges.subscribe((val) =>
      this.importSetup.controls.marketContextNewValue.setValue(val)
    );

    this.formsSubscribe = this.importSetup.controls.resource.valueChanges.subscribe((val) =>
      this.importSetup.controls.resourceNewValue.setValue(val)
    );
  }

  save() {
    // First validate that all required fields are set
    const missingFields = this.displayed_potential_rids
      .filter(rid => rid.included && !rid.market_context)
      .length > 0;

    if (missingFields) {
      this.error = 'One or more rid set to be created is missing a field. (market contexts are required to be set).';
      return;
    }

    this.status = 'Saving new RIDs...';

    // Filter only included RIDs
    this.importRid.mrids = this.displayed_potential_rids.filter(rid => rid.included);

    this.importRidsService.createPotentialRids(this.importRid).subscribe({
      next: (response: any) => {
        this.status = 'Finished successfully';
        goBack('/rids', this.router);
      },
      error: (err) => {
        console.error(err);
        this.error = err;
        this.status = 'Failed due to error.';
      }
    });
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  useMarketContext(event: any) {
    this.selectedMC = this.market_contexts.find((mc: any) => mc.id === Number(event.value)) ?? this.selectedMC;
    this.setup.operator = this.selectedMC.operator;
    this.setup.program = this.selectedMC.program;
    this.setup.id = this.selectedMC.product;
    if (this.setup.id) {
      this.setup.by = 'PRODUCT';
    }
    this.setFormsDefaultValue();
  }

  useDataSource(event: any) {
    this.setup.data_source = event.value;
    this.displayed_potential_rids = [];
    this.isSetup = false;
  }

  useImportBy(event: any) {
    this.setup.by = event.value;
    this.displayed_potential_rids = [];
    this.isSetup = false;
  }

  setupData() {
    if (!this.setup.id) {
      this.importRidsService
        .potentialRids(
          this.setup.by,
          this.setup.data_source,
          2,
          this.selectedMC.operator,
          this.selectedMC.program
        )
        .subscribe({
          next: (potentialRids: any) => {
            if (potentialRids && potentialRids.mrids) {
              this.isSetup = true;
              this.processRids(potentialRids.mrids);
            } else {
              this.isSetup = false;
            }
          },
          error: (err) => {
            console.error(err);
            this.error = err;
          }
        });
    } else {
      this.importRidsService
        .potentialRids(
          this.setup.by,
          this.setup.data_source,
          2,
          this.selectedMC.operator,
          this.selectedMC.program,
          this.setup.id
        )
        .subscribe({
          next: (potentialRids: any) => {
            if (potentialRids && potentialRids.mrids) {
              this.isSetup = true;
              this.processRids(potentialRids.mrids);
            } else {
              this.isSetup = false;
            }
          },
          error: (err) => {
            console.error(err);
            this.error = err;
          }
        });
    }
  }

  processRids(rids: any[]) {
    this.displayed_potential_rids = rids.map(rid => {
      return {
        ...rid,
        included: false,
        data_source: this.setup.data_source,
        market_context: this.selectedMC ? this.selectedMC.market_context : '',
        search: [
          rid.mrid?.toString(),
          rid.external_id,
          rid.division,
          rid.display_label,
        ].join(' ').toLowerCase()
      };
    });
  }

  powerType(event: any) {
    const ptSelected = this.channel_powertypes.find(
      (cpData: any) => event.value === cpData.uuid
    )?.uuid;
    this.importSetup.controls.powerTypeNewValue.setValue(ptSelected);
  }

  metada(event: any) {
    const getSelected = Array.from(event.options)
      .filter((ev: any) => ev.selected)
      .map((evtSelected: any) => evtSelected.value);
    this.metadaSelected = this.metadata_reports
      .filter((metada: any) => getSelected.includes(metada.id.toString()))
      .map((selected: any) => selected.id);
  }

  includedRids(rid: any, event: any) {
    if (!event.checked) {
      this.importRid.mrids.splice(this.importRid.mrids.findIndex((ridData: any) => ridData.mrid === rid.mrid), 1);
    } else {
      this.importRid.mrids.push({
        ...rid,
        included: event.checked,
        data_source: this.setup.data_source,
        market_context: this.selectedMC.market_context,
      });
    }
    if (this.importRid.mrids && this.importRid.mrids.length <= 0) {
      this.importRid = {
        mrids: [],
      };
    }
  }

  applyAll(type: string) {
    if (type === 'included') {
      this.displayed_potential_rids = this.displayed_potential_rids.map((rid: any) => {
        return {
          ...rid,
          included: true
        };
      });
      this.importRid.mrids = [...this.displayed_potential_rids];
    } else {
      let value: any = null;
      switch (type) {
        case 'resource':
          value = this.all.resource;
          break;
        case 'market_context':
          value = this.all.market_context;
          break;
        case 'metadata_reports':
          value = this.all.metadata_reports;
          break;
        case 'power_type':
          value = this.all.power_type;
          break;
        default:
          console.warn(`Unknown property type: ${type}`);
          break;
      }

      this.displayed_potential_rids = this.displayed_potential_rids.map((rid: any) => {
        if (rid.included) {
          return {
            ...rid,
            [type]: value
          };
        }
        return rid;
      });

      this.importRid.mrids = this.importRid.mrids.map((rid: any) => {
        return {
          ...rid,
          [type]: value
        };
      });
    }
  }

  clearAll(type: string) {
    if (type === 'included') {
      this.displayed_potential_rids = this.displayed_potential_rids.map((rid: any) => {
        return {
          ...rid,
          included: false
        };
      });
      this.importRid.mrids = [];
    } else {
      this.displayed_potential_rids = this.displayed_potential_rids.map((rid: any) => {
        if (rid.included) {
          return {
            ...rid,
            [type]: null
          };
        }
        return rid;
      });

      this.importRid.mrids = this.importRid.mrids.map((rid: any) => {
        return {
          ...rid,
          [type]: null
        };
      });
    }
  }
}
